import React from "react";
import PropTypes from "prop-types";

/**
 * Inline chip strip used in the Add / Update Diagnosis dialogs for the three
 * "link a related entry" sections (medicines, chief complaints, labs).
 *
 * Shows at most `maxInline` chips inline (default 2) — the rest collapse into
 * a `+N more` pill that opens the picker modal. Selected chips render with a
 * check mark; unselected with a `+` sign. Clicking an inline chip toggles it;
 * the picker is the full multi-select surface.
 *
 * Empty state: when `items` is empty, renders a soft "No <label> recorded
 * yet · add them first" message instead of the chip strip.
 */
const CheckIcon = () => (
  <svg className="dx-strip-check" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
    <path d="M4 10.5l4 4 8-9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const PlusIcon = () => (
  <svg className="dx-strip-plus" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M10 4v12M4 10h12" strokeLinecap="round" />
  </svg>
);
const SparkleIcon = () => (
  <svg className="dx-strip-sparkle" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path d="M10 1l2.2 5.2L18 8l-5.8 1.8L10 15l-2.2-5.2L2 8l5.8-1.8L10 1z" />
  </svg>
);

const LinkedRefsChipStrip = ({
  title,
  suggestionLabel,
  emptyMessage,
  items,
  selectedIds,
  getKey,
  getLabel,
  getStatusMark,
  onToggle,
  onOpenPicker,
  minInline,
}) => {
  const minShown = minInline || 2;
  const isEmpty = !items || items.length === 0;

  const selectedSet =
    selectedIds instanceof Set ? selectedIds : new Set(selectedIds || []);
  const isSelected = (item) => selectedSet.has(getKey(item));

  // Display rule (per UX):
  //   - Always show ALL selected items (no cap).
  //   - If < minShown selected, top up with unselected items to reach minShown.
  //   - "more" button:
  //       * "+ N more" when N > 0 unselected items remain unshown
  //       * "more" (no count) when everything is already visible AND
  //         everything is selected — the button still opens the picker
  //         so the user can deselect.
  const allSelected = items || [];
  const selectedItems = allSelected.filter(isSelected);
  const unselectedItems = allSelected.filter((it) => !isSelected(it));

  const fillCount = Math.max(0, minShown - selectedItems.length);
  const filledUnselected = unselectedItems.slice(0, fillCount);
  const inlineItems = [...selectedItems, ...filledUnselected];

  const remainingUnselected = unselectedItems.length - filledUnselected.length;
  const hasMore = remainingUnselected > 0 || (!isEmpty && allSelected.length === selectedItems.length);
  const moreLabel = remainingUnselected > 0 ? `+ ${remainingUnselected} more` : "more";

  return (
    <div className="dx-strip">
      <div className="dx-strip-head">
        <span className="dx-strip-title">{title}</span>
      </div>
      {suggestionLabel && (
        <p className="dx-strip-suggest">
          <SparkleIcon />
          <span>{suggestionLabel}</span>
        </p>
      )}
      {isEmpty ? (
        <p className="dx-strip-empty">{emptyMessage}</p>
      ) : (
        <div className="dx-strip-chips">
          {inlineItems.map((item) => {
            const on = isSelected(item);
            const mark = getStatusMark ? getStatusMark(item) : null;
            const label = getLabel(item);
            return (
              <button
                key={getKey(item)}
                type="button"
                className={`dx-strip-chip${on ? " is-on" : ""}`}
                onClick={() => onToggle(getKey(item), item)}
                aria-pressed={on}
                title={label}
              >
                {on ? <CheckIcon /> : <PlusIcon />}
                <span className="dx-strip-chip-label">{label}</span>
                {mark && <span className="dx-strip-chip-mark">{mark}</span>}
              </button>
            );
          })}
          {hasMore && (
            <button
              type="button"
              className="dx-strip-more"
              onClick={onOpenPicker}
            >
              {moreLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

LinkedRefsChipStrip.propTypes = {
  title: PropTypes.string.isRequired,
  suggestionLabel: PropTypes.string,
  emptyMessage: PropTypes.string.isRequired,
  items: PropTypes.array,
  selectedIds: PropTypes.oneOfType([
    PropTypes.array,
    PropTypes.instanceOf(Set),
  ]),
  getKey: PropTypes.func.isRequired,
  getLabel: PropTypes.func.isRequired,
  getStatusMark: PropTypes.func,
  onToggle: PropTypes.func.isRequired,
  onOpenPicker: PropTypes.func.isRequired,
  minInline: PropTypes.number,
};

export default LinkedRefsChipStrip;
